import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

/**
 * LightningArresterForm — single-level approval flow
 *
 * Status pipeline (same logic as APFC but single level):
 *   draft → CONTRACTOR → MANAGER → QUALITY → approved
 *
 * Editability:
 *   - Equipment/Checks/Observations: Engineer can edit only when status === 'draft'. Others read-only.
 *   - General Comments (other_comments): editable by ALL roles at ALL statuses UNTIL approved.
 *   - Comments/Signatures table:
 *       • charge_engineer: editable by Engineer when status === 'draft'
 *       • contractor: editable by Contractor when status === 'CONTRACTOR'
 *       • quality: editable by Quality when status === 'QUALITY'
 *       • optional: always read-only
 *
 * Buttons:
 *   - Engineer (status==='draft'): Submit / Save Draft (both escalate to CONTRACTOR, matching APFC behavior)
 *   - Contractor (status==='CONTRACTOR'): Submit / Save Draft (both escalate to MANAGER)
 *   - Manager (status==='MANAGER'): Approve (→ QUALITY) / Re-Work (→ draft)
 *   - Quality (status==='QUALITY'): Approve (→ approved) / Apply for Resubmit (→ draft)
 *   - Admin: read-only
 */

const LightningArresterForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      serial_number: '',
      height_structure: '',
      la_make_model: '',
      roof_type: '',
      visual_checks: Array(13).fill({ status: '', remark: '' }),
      observations: { a: '', b: '', c: '' },
      other_comments: '', // NEW: general comments open to all until approved
      comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      status: 'draft',
      manager_approve: false,
    },
  });

  const formValues = useWatch({ control });
  const status = useWatch({ control, name: 'status' });

  const autoSave = useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const payload = {
        form_data: {
          status: data.status,
          form_id: formId,
          formName: 'lightning_arrester',
          station_id: data.station_id,
          project_name: data.project_name,
          serial_number: data.serial_number,
          height_structure: data.height_structure,
          la_make_model: data.la_make_model,
          roof_type: data.roof_type,
          visual_checks: data.visual_checks,
          observations: data.observations,
          other_comments: data.other_comments, // include in autosave
          comments: data.comments,
          manager_approve: data.manager_approve,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
    autoSave(formValues);
  }, [formValues, autoSave]);

  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'lightning_arrester' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setValue('station_id', data.station_id || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('serial_number', data.serial_number || '');
      setValue('height_structure', data.height_structure || '');
      setValue('la_make_model', data.la_make_model || '');
      setValue('roof_type', data.roof_type || '');
      setValue(
        'visual_checks',
        Array.isArray(data.visual_checks)
          ? data.visual_checks
          : Array(13).fill({ status: '', remark: '' })
      );
      setValue('observations', data.observations || { a: '', b: '', c: '' });
      setValue('other_comments', data.other_comments || ''); // NEW: hydrate from DB
      setValue(
        'comments',
        data.comments || {
          contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        }
      );
      setValue('status', data.status || 'draft');
      setValue('manager_approve', !!data.manager_approve);
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // Helpers for permissions
  const isFieldsEditable = (role === 'engineer' && status === 'draft') || (role === 'contractor' && status === 'CONTRACTOR');
  const isOtherCommentsEditable = status !== 'approved'; // NEW: editable by ALL roles until approved

  const canEditSignature = useCallback(
    (sigRole) => {
      const s = getValues('status');
      if (role === 'engineer' && sigRole === 'charge_engineer' && s === 'draft') return true;
      if (role === 'contractor' && sigRole === 'contractor' && s === 'CONTRACTOR') return true;
      if (role === 'quality' && sigRole === 'quality' && s === 'QUALITY') return true;
      return false; // optional & others are read-only
    },
    [role, getValues]
  );

  // Signature handlers
  const handleImageUpload = (e, sigRole) => {
    if (!canEditSignature(sigRole)) return;
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB limit
    if (file) {
      if (file.size > maxSizeInBytes) {
        alert('File size exceeds 50kb. Please upload a smaller image.');
        handleResetSignature(sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (sigRole, type) => {
    if (!canEditSignature(sigRole)) return;
    setValue(`comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (sigRole) => {
    if (!canEditSignature(sigRole)) return;
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    const sigRole = currentSignature.role;
    if (!canEditSignature(sigRole)) {
      setShowModal(false);
      return;
    }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`comments.${sigRole}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // Submit logic (mirrors APFC role overrides)
  const onSubmit = (action) => (data) => {
    let nextStatus = data.status;
    let managerApprove = data.manager_approve;

    if (role === 'engineer') {
      nextStatus = 'CONTRACTOR';
    } else if (role === 'contractor') {
      nextStatus = 'MANAGER';
    } else if (role === 'manager' && action === 'approved') {
      nextStatus = 'QUALITY';
      managerApprove = true;
    } else if (role === 'manager' && action === 'draft') {
      nextStatus = 'draft';
    } else if (role === 'quality' && action === 'approved') {
      nextStatus = 'approved';
    } else if (role === 'quality' && action === 'draft') {
      nextStatus = 'draft';
    }

    const payload = {
      form_data: {
        status: nextStatus,
        form_id: formId,
        formName: 'lightning_arrester',
        station_id: data.station_id,
        project_name: data.project_name,
        serial_number: data.serial_number,
        height_structure: data.height_structure,
        la_make_model: data.la_make_model,
        roof_type: data.roof_type,
        visual_checks: data.visual_checks,
        observations: data.observations,
        other_comments: data.other_comments, // include in submit
        comments: data.comments,
        manager_approve: managerApprove,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });

    // Keep toast text consistent with APFC (uses original action, not the overridden status)
    toast.success(`Form ${action === 'pending' || action === 'approved' ? 'Submitted' : 'Saved as Draft'} Successfully`);
    navigate(-1);
  };

  // Button enable/disable policy (same as APFC)
  const submitDisabled =
    role === 'engineer' ? status !== 'draft' : role === 'contractor' ? status !== 'CONTRACTOR' : true;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">Lightning Arrester - Site Acceptance Testing</h2>

      {role === 'engineer' && status !== 'draft' && (
        <div className="alert alert-warning mb-4">This form is in View Mode Only for Engineer.</div>
      )}

      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!isFieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control type="text" {...register('serial_number')} disabled={!isFieldsEditable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Height of Structure/Mast</Form.Label>
              <Form.Control type="text" {...register('height_structure')} disabled={!isFieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>LA Make & Model</Form.Label>
              <Form.Control type="text" {...register('la_make_model')} disabled={!isFieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Type of Roof for Installation</Form.Label>
              <Form.Select {...register('roof_type')} disabled={!isFieldsEditable}>
                <option value="">Select</option>
                <option value="Flat roof">Flat roof</option>
                <option value="Metal roof">Metal roof</option>
                <option value="Separate Mast">Separate Mast</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <h3 className="mt-4">2. Visual Checks</h3>
        <Table bordered>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Description</th>
              <th>Status</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {[
              'Is the air terminal free from corrosion, dents, or any damage?',
              'Is the physical condition of the down conductor satisfactory?',
              'Is the air terminal located in an open area, or is it under the shadow of any building, tower, or mast?',
              'Is separate earth connection provided?',
              'What is the condition and number of earth connections?',
              'Is the routing of the down conductor appropriate to prevent side flash issues?',
              'Are insulation supports provided for the down conductors?',
              'Is there a test link present?',
              'Is there one test link provided for each down conductor?',
              'Is surge protection provided for field instruments, telecommunication equipment, etc.?',
              'Is the air terminal installed as per the marked locations on the plan/elevation drawing?',
              'Is a surge counter (digital or analogue) present?',
              'Are all joints properly fixed as per the typical installation guidelines?',
            ].map((desc, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{desc}</td>
                <td>
                  <Form.Check
                    inline
                    label="Yes"
                    type="radio"
                    name={`visual_checks_${index}`}
                    value="Yes"
                    {...register(`visual_checks.${index}.status`)}
                    disabled={!isFieldsEditable}
                  />
                  <Form.Check
                    inline
                    label="No"
                    type="radio"
                    name={`visual_checks_${index}`}
                    value="No"
                    {...register(`visual_checks.${index}.status`)}
                    disabled={!isFieldsEditable}
                  />
                  <Form.Check
                    inline
                    label="NA"
                    type="radio"
                    name={`visual_checks_${index}`}
                    value="NA"
                    {...register(`visual_checks.${index}.status`)}
                    disabled={!isFieldsEditable}
                  />
                </td>
                <td>
                  <Form.Control
                    type="text"
                    {...register(`visual_checks.${index}.remark`)}
                    disabled={!isFieldsEditable}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h3 className="mt-4">3. Observations during Commissioning / Testing (If Any)</h3>
        {['a', 'b', 'c'].map((key) => (
          <Form.Group className="mb-3" key={key}>
            <Form.Label>{key.toUpperCase()}.</Form.Label>
            <Form.Control type="text" {...register(`observations.${key}`)} disabled={!isFieldsEditable} />
          </Form.Group>
        ))}

        <h3 className="mt-4">4. General Comments / Remarks</h3>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={4}
            {...register('other_comments')}
            disabled={!isOtherCommentsEditable}
          />
        </Form.Group>

        <h3 className="mt-4">5. Comments</h3>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Engineer - Contractor</th>
              <th>Engineer - Charge Zone</th>
              <th>Quality - Charge Zone</th>
              <th>Optional</th>
            </tr>
          </thead>
          <tbody>
            {['Name', 'Company', 'Signature', 'Date'].map((field) => (
              <tr key={field}>
                <td>{field}</td>
                {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => {
                  const editable = canEditSignature(sigRole);
                  if (field === 'Date') {
                    return (
                      <td key={sigRole}>
                        <Form.Control
                          type="date"
                          {...register(`comments.${sigRole}.date`)}
                          disabled={!editable}
                        />
                      </td>
                    );
                  } else if (field === 'Signature') {
                    return (
                      <td key={sigRole}>
                        <Form.Select
                          {...register(`comments.${sigRole}.signatureType`)}
                          onChange={(e) => handleSignatureTypeChange(sigRole, e.target.value)}
                          className="mb-2"
                          disabled={!editable}
                        >
                          <option value="upload">Image Upload</option>
                          <option value="digital">Digital Sign</option>
                        </Form.Select>
                        {_.get(formValues, `comments.${sigRole}.signatureType`) === 'upload' && (
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, sigRole)}
                            className="mb-2"
                            disabled={!editable}
                          />
                        )}
                        {_.get(formValues, `comments.${sigRole}.signature`) && (
                          <div>
                            <img
                              src={_.get(formValues, `comments.${sigRole}.signature`)}
                              alt="Signature"
                              style={{ maxWidth: '100px', marginTop: '5px' }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleResetSignature(sigRole)}
                              className="mt-2"
                              disabled={!editable}
                            >
                              Reset Signature
                            </Button>
                          </div>
                        )}
                      </td>
                    );
                  }
                  return (
                    <td key={sigRole}>
                      <Form.Control
                        type="text"
                        {...register(`comments.${sigRole}.${field.toLowerCase()}`)}
                        disabled={!editable}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Manager & Quality action bars */}
        {role === 'manager' && status === 'MANAGER' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approved'))} className="me-2">
              Approve
            </Button>
            <Button variant="danger" onClick={handleSubmit(onSubmit('draft'))}>
              Re-Work
            </Button>
          </div>
        ) : role === 'quality' && status === 'QUALITY' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approved'))} className="me-2">
              Approve
            </Button>
            <Button variant="danger" onClick={handleSubmit(onSubmit('draft'))}>
              Apply for Resubmit
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('pending'))} disabled={submitDisabled}>
              Submit Form
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleSubmit(onSubmit('draft'))}
              disabled={submitDisabled}
            >
              Save As Draft
            </Button>
          </div>
        )}

        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    </div>
  );
};

LightningArresterForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
};

export default LightningArresterForm;
